import React from 'react';

export interface GaugeDisplayProps {
  value: number | null;
  min?: number | null | undefined;
  max?: number | null | undefined;
  unit?: string | undefined;
}

// ─── Geometry ─────────────────────────────────────────────────────────────────
// Arc: 240° span, from SVG 150° (8 o'clock) to SVG 30° (4 o'clock), clockwise
// through the top (SVG 270° = 12 o'clock).  All angles are SVG convention:
// 0° = 3 o'clock, increasing clockwise, y-axis pointing down.

const CX = 100;
const CY = 92;
const R = 66;
const SW = 17;
const START_DEG = 150;
const SPAN_DEG = 240;

function toXY(svgDeg: number, r: number): [number, number] {
  const rad = (svgDeg * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
}

function zonePath(t1: number, t2: number): string {
  const a1 = START_DEG + t1 * SPAN_DEG;
  const a2 = START_DEG + t2 * SPAN_DEG;
  const [x1, y1] = toXY(a1, R);
  const [x2, y2] = toXY(a2, R);
  const spanDeg = (t2 - t1) * SPAN_DEG;
  const largeArc = spanDeg > 180 ? 1 : 0;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

// ─── Arc zones — always fixed proportions ─────────────────────────────────────
// The visual arc segments never change regardless of min/max field values.
// Only the needle position and status label respond to the actual value.

const ZONES: { t1: number; t2: number; color: string }[] = [
  { t1: 0, t2: 0.32, color: '#FF9800' },
  { t1: 0.32, t2: 0.68, color: '#00AB76' },
  { t1: 0.68, t2: 1.0, color: '#E11B1B' },
];

// ─── Value → gauge fraction [0, 1] ───────────────────────────────────────────
// value <= min  → needle at far left  (pct = 0, start of orange zone)
// value in (min, max) → needle within green zone (0.32 → 0.68)
// value >= max  → needle at far right (pct = 1, end of red zone)
// When min/max absent, maps linearly from [0, max].

function getGaugePct(
  value: number,
  min: number | null | undefined,
  max: number | null | undefined
): number {
  if (min == null || max == null || min >= max) {
    return max != null && max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0;
  }
  if (value <= min) {
    return 0;
  }
  if (value >= max) {
    return 1;
  }
  return 0.32 + 0.36 * ((value - min) / (max - min));
}

// ─── Status from value vs min/max thresholds ──────────────────────────────────

function getZoneStatus(
  value: number,
  min: number | null | undefined,
  max: number | null | undefined
): { label: string; color: string } {
  if (min != null && value < min) {
    return { label: 'Niski', color: '#FF9800' };
  }
  if (max != null && value > max) {
    return { label: 'Wysoki', color: '#E11B1B' };
  }
  return { label: 'Normalny', color: '#00AB76' };
}

// ─── Component ────────────────────────────────────────────────────────────────

const GaugeDisplay: React.FC<GaugeDisplayProps> = ({
  value,
  min,
  max,
  unit,
}) => {
  const hasValue = value !== null;
  const pct = hasValue ? getGaugePct(value, min, max) : 0;
  const status = hasValue ? getZoneStatus(value, min, max) : null;
  const zones = ZONES;

  const needleAngleDeg = START_DEG + pct * SPAN_DEG;
  const needleLen = R - SW / 2 - 4;
  const [nx, ny] = toXY(needleAngleDeg, needleLen);

  const displayValue = value ?? 0;

  return (
    <svg
      width={200}
      height={152}
      viewBox="0 0 200 152"
      aria-label={`Gauge: ${displayValue}${unit ? ` ${unit}` : ''}${status ? `, ${status.label}` : ''}`}
    >
      {/* Background track */}
      <path
        d={zonePath(0, 1)}
        fill="none"
        stroke="#E5E7E9"
        strokeWidth={SW}
        strokeLinecap="butt"
      />

      {/* Colour zones */}
      {zones.map((zone, i) => (
        <path
          key={i}
          d={zonePath(zone.t1, zone.t2)}
          fill="none"
          stroke={zone.color}
          strokeWidth={SW}
          strokeLinecap="butt"
        />
      ))}

      {/* Rounded end caps */}
      {(() => {
        const [sx, sy] = toXY(START_DEG, R);
        const [ex, ey] = toXY(START_DEG + SPAN_DEG, R);
        return (
          <>
            <circle
              cx={sx.toFixed(2)}
              cy={sy.toFixed(2)}
              r={SW / 2}
              fill={zones[0]?.color ?? '#FF9800'}
            />
            <circle
              cx={ex.toFixed(2)}
              cy={ey.toFixed(2)}
              r={SW / 2}
              fill={zones[zones.length - 1]?.color ?? '#E11B1B'}
            />
          </>
        );
      })()}

      {/* Needle */}
      <line
        x1={CX}
        y1={CY}
        x2={nx.toFixed(2)}
        y2={ny.toFixed(2)}
        stroke="#181A1B"
        strokeWidth={2.5}
        strokeLinecap="round"
      />

      {/* Centre cap */}
      <circle cx={CX} cy={CY} r={5} fill="#181A1B" />

      {/* Numeric value */}
      <text
        x={CX}
        y={CY + 25}
        textAnchor="middle"
        fontSize={20}
        fontWeight={500}
        fill="#181A1B"
        fontFamily="Roboto, sans-serif"
      >
        {displayValue}
      </text>

      {/* Status label */}
      {status && (
        <text
          x={CX}
          y={CY + 40}
          textAnchor="middle"
          fontSize={11}
          fill={status.color}
          fontFamily="Roboto, sans-serif"
        >
          {status.label}
        </text>
      )}
    </svg>
  );
};

export default GaugeDisplay;
